import { BalanceData, BossMechanic, LevelData } from "../data/balance";
import { Fix64 } from "../maths/fix64";
import { Fnv1a64 } from "../maths/fnv1a64";
import { Pcg32 } from "../maths/pcg32";
import { ResolvedModifiers } from "./modifiers";
import { SpawnEntry, buildWaveSchedule } from "./waveSchedule";
import { markImmune } from "./immunity";

/**
 * The waves of the two run modes (docs/MODES.md, balance/src/modes.json):
 *
 * - **Iron**: the level's every stage in ONE wave — stage s's spawns (at stage s's power, with the level's elites) as a
 *   block, the next block starting `blockGapTicks` after the block's last spawn, the boss closing the last block. The
 *   run's HP modifier (the Iron multiplier) applies to all of it. The draft deals only the run's Iron towers
 *   (LevelSim masks `owned`).
 * - **Endless**: waves without end, built from the data's curve and the run's seed; see `endlessWave`.
 *
 * Pure: the wave preview and the run build the same schedule from the same inputs. A plain level's stages also carry its
 * immune units (sim/immunity.ts markImmune, M36).
 */

/**
 * The grunt's kill points: Endless sizes a wave in grunt-equivalents and a type's count is G/k × this / its own kill
 * points — points are the balance's measure of what a raider is worth, so support types (healers, bombers) come in
 * fewer and brutes in fewer still, and a new enemy arrives sized by its own points.
 */
const GE_POINTS = 5;
/** Tags the per-wave stream of an Endless run's type picks, apart from the run's own RNG. */
const ENDLESS_WAVE_TAG = "endlessWave";

/** The stage count of a run: one wave for Iron, the wave cap for Endless, the day's for Daily Road, an event mode's own, else the level's. */
export function runStages(balance: BalanceData, levelNumber: number, mods: ResolvedModifiers): number {
  if (mods.endless) return balance.modes.endless.waveCap;
  if (mods.iron) return 1;
  // Daily Road (MP-20): the level's first `stages` stages (never more than it has), the boss closing the last.
  if (mods.daily) return Math.max(1, Math.min(mods.daily.stages, balance.stagesOf(levelNumber)));
  // MP-21 event modes: the same, the layout's first `stages` with its boss closing the last.
  if (mods.event) return Math.max(1, Math.min(mods.event.stages, balance.stagesOf(levelNumber)));
  return balance.stagesOf(levelNumber);
}

/** The spawns of `stage` for this run (Iron, Endless or a plain level), as LevelSim.startStage and previewStage build them. */
export function stageSchedule(balance: BalanceData, level: LevelData, levelNumber: number, stage: number, stages: number, startDelay: number, mods: ResolvedModifiers, seed: bigint): SpawnEntry[] {
  if (mods.endless) return endlessWave(balance, mods.endless.through, stage, seed, startDelay);
  if (mods.iron) return ironWave(balance, level, levelNumber, startDelay, mods);
  // M36: a plain level's immune units (sim/immunity.ts); Iron and Endless keep their own waves
  return markImmune(balance, levelNumber, stage, buildWaveSchedule(balance, level, levelNumber, stage, stages, startDelay, mods), seed);
}

/** Iron's one wave: every stage of the level as consecutive blocks (see the module note). */
export function ironWave(balance: BalanceData, level: LevelData, levelNumber: number, startDelay: number, mods: ResolvedModifiers, rareBlock = 0, rare: ((tick: number) => SpawnEntry) | null = null,
  wyrmBlock = 0, wyrm: ((tick: number) => SpawnEntry) | null = null): SpawnEntry[] {
  const stages = balance.stagesOf(levelNumber), gap = balance.modes.iron.blockGapTicks;
  const out: SpawnEntry[] = [];
  let offset = startDelay;
  for (let s = 1; s <= stages; s++) {
    // stages + 1 for every block but the last keeps the boss out of them; the last block's boss closes the wave.
    const block = buildWaveSchedule(balance, level, levelNumber, s, s === stages ? stages : stages + 1, 0, mods);
    let last = 0;
    // M37 (sim/rares.ts): a rare block is ONE rare at the block's start; the blocks after it keep their ticks. M40: the Wyrm's alike.
    const special = s === wyrmBlock && wyrm !== null ? wyrm : s === rareBlock && rare !== null ? rare : null;
    if (special !== null) out.push(special(offset));
    for (const e of block) { if (special === null) out.push({ ...e, tick: e.tick + offset }); if (e.tick > last) last = e.tick; }
    offset += last + gap;
  }
  return out;
}

/**
 * Endless wave power P(w): levelBase(through) multiplied wave by wave by the growth step in force (modes.json
 * `growth`), capped at `powerCap`. Repeated Q32.32 multiplication: exact and the same everywhere, no pow at runtime.
 */
export function endlessPower(balance: BalanceData, through: number, wave: number): Fix64 {
  const d = balance.modes.endless, t = balance.tables;
  let p = t.levelBaseAt(Math.max(1, Math.min(through, t.maxLevel)));
  let step = 0;
  for (let w = 2; w <= wave; w++) {
    while (step + 1 < d.growth.length && d.growth[step + 1].fromWave <= w) step++;
    p = p.mul(d.growth[step].rate);
    if (p.gt(d.powerCap)) return d.powerCap;
  }
  return p.gt(d.powerCap) ? d.powerCap : p;
}

/**
 * Endless's raider pool: every non-boss enemy unlocked by `through` (balance order), so new enemies join on their own —
 * except a summoned-only one (the Imp: some archetype's `summonEnemy`), which comes with its Summoner, never as a group,
 * and a rare (M36), which comes alone by its own roll (sim/rares.ts), as does the Starfall Wyrm (M40, sim/wyrm.ts).
 */
export function endlessPool(balance: BalanceData, through: number): number[] {
  const summoned = new Set(balance.enemies.map((e) => e.abilities.summonEnemy).filter((i) => i >= 0));
  // a rare (M36, sim/rares.ts) is never a group: it comes alone, by its own roll
  return balance.enemies.filter((e) => e.bossMechanic === BossMechanic.None && e.rareChapter === 0 && !e.legend && e.wildPet === "" && e.unlockLevel <= through && !summoned.has(e.index)).map((e) => e.index);
}
/** Endless's bosses, in turn every `bossEvery` waves: the bosses unlocked by `through`. */
export function endlessBosses(balance: BalanceData, through: number): number[] {
  return balance.enemies.filter((e) => e.bossMechanic !== BossMechanic.None && e.unlockLevel <= through).map((e) => e.index);
}

/** Grunt-equivalents of wave `w` (its size before power): geBase + gePerWave × (w − 1), capped at geCap. */
export function endlessSize(balance: BalanceData, wave: number): number {
  const d = balance.modes.endless;
  return Math.min(d.geBase + d.gePerWave * (wave - 1), d.geCap);
}
/** How many raider types wave `w` mixes. */
export function endlessTypes(balance: BalanceData, wave: number, poolSize: number): number {
  const d = balance.modes.endless;
  return Math.max(1, Math.min(d.typesBase + Math.floor((wave - 1) / d.typesEveryWaves), d.typesMax, poolSize));
}
/** Whether wave `w` brings a boss. */
export function endlessBossWave(balance: BalanceData, wave: number): boolean {
  const every = balance.modes.endless.bossEvery;
  return every > 0 && wave % every === 0;
}
/** From eliteFromWave: every how many-th raider wears an affix on wave `w` (0 = none), tightening every eliteTightenWaves. */
export function endlessEliteEvery(balance: BalanceData, wave: number): number {
  const d = balance.modes.endless;
  if (wave < d.eliteFromWave || balance.affixes.length === 0) return 0;
  return Math.max(d.eliteEveryMin, d.eliteEvery - Math.floor((wave - d.eliteFromWave) / d.eliteTightenWaves));
}

function waveRng(seed: bigint, wave: number): Pcg32 {
  const h = new Fnv1a64();
  h.writeString(ENDLESS_WAVE_TAG);
  h.writeLong(seed);
  h.writeInt(wave);
  return new Pcg32(h.value);
}

/**
 * Endless wave `wave` (modes.json `endless`): k types drawn from the pool by the wave's own stream (seed, wave), each
 * a group of G/k grunt-equivalents at power P(w), the groups one after another; a boss every bossEvery waves; elite
 * affixes from eliteFromWave (one affix a wave, in turn). Pure and seeded: the preview and the run agree.
 */
export function endlessWave(balance: BalanceData, through: number, wave: number, seed: bigint, startDelay: number): SpawnEntry[] {
  const d = balance.modes.endless;
  const pool = endlessPool(balance, through);
  const out: SpawnEntry[] = [];
  if (pool.length === 0) return out;
  const power = endlessPower(balance, through, wave);
  const rng = waveRng(seed, wave);
  const left = [...pool];
  const types: number[] = [];
  const k = endlessTypes(balance, wave, pool.length);
  for (let i = 0; i < k; i++) types.push(left.splice(rng.nextInt(left.length), 1)[0]);
  const share = endlessSize(balance, wave) * GE_POINTS;
  let start = startDelay, lastTick = startDelay;
  for (const archetype of types) {
    const points = Math.max(1, balance.enemies[archetype].points);
    const count = Math.max(1, Math.min(d.groupCap, Math.round(share / (k * points))));
    for (let i = 0; i < count; i++) {
      const tick = start + i * d.intervalTicks;
      out.push({ tick, archetype, hpMult: power, isBoss: false, pointsOverride: -1 });
      if (tick > lastTick) lastTick = tick;
    }
    start += Math.floor((count * d.intervalTicks) / 2) + d.groupGapTicks;
  }
  const every = endlessEliteEvery(balance, wave);
  if (every > 0) {
    const affix = (wave - d.eliteFromWave) % balance.affixes.length;
    // A raider that never wears an affix (the Spellward: `noAffix`, ENEMIES_V2) is skipped and not counted, as in markElites.
    let n = 0;
    out.forEach((e, i) => { if (!balance.enemies[e.archetype].abilities.noAffix && ++n % every === 0) out[i] = { ...e, affix }; });
  }
  const bosses = endlessBosses(balance, through);
  if (endlessBossWave(balance, wave) && bosses.length > 0) {
    const boss = bosses[(wave / d.bossEvery - 1) % bosses.length];
    out.push({ tick: lastTick + balance.economy.bossDelayTicks, archetype: boss, hpMult: power.mul(balance.economy.bossHpMult), isBoss: true, pointsOverride: balance.economy.bossPoints });
  }
  // Stable insertion sort by tick (the groups overlap): equal ticks keep group order.
  for (let i = 1; i < out.length; i++) {
    const key = out[i];
    let j = i - 1;
    while (j >= 0 && out[j].tick > key.tick) { out[j + 1] = out[j]; j--; }
    out[j + 1] = key;
  }
  return out;
}
